/**
 * weatherMap — GET /api/weather (flask weather_service: Open-Meteo, MET Norway
 * fallback, Redis-cached) -> the campsite page's WeatherForecast shape.
 */
import type { WeatherDay, WeatherForecast } from '../types';

/** One day of GET /api/weather (flask weather_service). */
interface ApiWeatherDay {
  date: string;
  temp_max_c: number | null;
  temp_min_c: number | null;
  rain_chance_pct: number | null;
  rain_mm: number | null;
  wind_max_kmh: number | null;
  uv_index_max: number | null;
  humidity_min_pct?: number | null;
  weather_code?: number | null;
  sunrise?: string;
  sunset?: string;
}

/** Map the flask /api/weather payload onto the campsite page's WeatherForecast. */
export function mapApiWeather(data: any): WeatherForecast | null {
  const daily: ApiWeatherDay[] | undefined = data?.daily_forecast;
  if (!Array.isArray(daily) || daily.length === 0) return null;
  const days: WeatherDay[] = daily.map((d) => ({
    date: d.date,
    tempMax: d.temp_max_c ?? 0,
    tempMin: d.temp_min_c ?? 0,
    precipitationProbability: d.rain_chance_pct ?? 0,
    precipitation: d.rain_mm ?? 0,
    windspeed: d.wind_max_kmh ?? 0,
    uvIndex: d.uv_index_max ?? 0,
    humidityMin: d.humidity_min_pct ?? 50,
    weatherCode: d.weather_code ?? 0,
    sunrise: d.sunrise ?? '',
    sunset: d.sunset ?? '',
  }));
  return {
    latitude: Number(data?.location?.latitude),
    longitude: Number(data?.location?.longitude),
    timezone: data?.location?.timezone ?? '',
    days,
    source: data?.source === 'met-norway' ? 'met-norway' : 'open-meteo',
  };
}

/** Credit line for the provider that actually supplied the forecast. */
export function weatherCredit(source?: WeatherForecast['source']): { label: string; href: string } {
  return source === 'met-norway'
    ? { label: 'Data from MET Norway', href: 'https://api.met.no/' }
    : { label: 'via Open-Meteo', href: 'https://open-meteo.com/' };
}
